<template>
	<component :is="labelComponent" class="block" :class="{ 'has-error': error, [wrapperClass]: true }">
        <form-label
			v-if="label"

            :component="labelComponent === 'label' ? 'span' : labelComponent"

            :class="{'text-red-500': error, [labelClass]: true}"

			:required="required"

            :label="label"
            :is-html="isHtml"
        >
			<slot name="label" />
		</form-label>

		<component 
			:is="formComponent" 
			v-bind="attrs" 
			ref="field" 

			:class="{ error: error }" 
			:type="type" 
			:required="required"
			
		>
			<template v-for="(slot, slotName) in slots" #[slotName]="slotProps">
				<slot :name="slotName" v-bind="slotProps" />
			</template>
		</component>
		<form-error v-if="error && !hideError" :error="error" />
	</component>
</template>
<script>
/**
 * This script is required as the only way to disable inheritance of passed attributes is with this object
 * @see https://vuejs.org/guide/components/attrs.html#disabling-attribute-inheritance
 */
export default {
	inheritAttrs: false,
};
</script>
<script setup>
import { useAttrs, computed, ref, useSlots, defineAsyncComponent } from "vue";
import FormError from "./FormError.vue";
import FormLabel from "./FormLabel.vue";

const FormCheckbox = defineAsyncComponent(() => import("./field-types/FormCheckbox.vue"));
const FormDateField = defineAsyncComponent(() => import("./field-types/FormDateField.vue"));
const FormWysiwygField = defineAsyncComponent(() => import("./field-types/FormWysiwygField.vue"));
const FormTextField = defineAsyncComponent(() => import("./field-types/FormTextField.vue"));
const FormSelect = defineAsyncComponent(() => import("./field-types/FormSelect.vue"));
const FormTextarea = defineAsyncComponent(() => import("./field-types/FormTextarea.vue"));
const FormFile = defineAsyncComponent(() => import("./field-types/FormFile.vue"));
const FormAddressLookup = defineAsyncComponent(() => import("./field-types/FormAddressLookup.vue"));
const FormCheckboxGroup = defineAsyncComponent(() => import("./field-types/FormCheckboxGroup.vue"));
const attrs = useAttrs();
const slots = useSlots();
const props = defineProps({
	labelComponent: {
		type: String,
		default: "span",
	},
	label: String,
	labelClass: String,
	isHtml: Boolean,
	error: String,
	required: Boolean,
	hideError: Boolean,
	type: {
		type: String,
		default: "text",
	},
	clearError: {
		type: Boolean,
		default: true,
	},
	wrapperClass: {
		type: String,
		default: "",
	},
});
const field = ref(null);
/**
 * Find the component we require for the defined input
 */
const formComponent = computed(() => {
	//Contains a list of 'types' a certain component should encapsulate
	//brackets surrounding the keys so we reference the actual component as the key
	const fields = new Map([
		[FormCheckbox, ["checkbox", "radio"]],
		[FormDateField, ["date", "datetime", "time"]],
		[FormWysiwygField, ["wysiwyg"]],
		[FormTextarea, ["textarea"]],
		[FormSelect, ["select"]],
		[FormFile, ["file"]],
		[FormAddressLookup, ["address-lookup"]],
		[FormCheckboxGroup, ["checkbox-group"]],
	]);
	//Find the element we should match from above, if one cannot be found default to a standard text field
	return [...fields.entries()].find((value) => value[1].includes(props.type))?.[0] ?? FormTextField;
});
/**
 * Finds the component to use for the wrapper of the form input
 *
 * Fix for an issue where replaced elements like the multi-select trigger the remove option on click
 * instead of triggering the select as expected
 */
const labelComponent = computed(() => {
	return [FormSelect].indexOf(formComponent.value) !== -1 ? "span" : props.labelComponent;
});
defineExpose({
	field: field,
});
</script>
